import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ReportTable, Section } from '../report-shell'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Info, Download } from '@mui/icons-material'
import { apiService } from '../../services/api'
import MetricCard from '../ui/MetricCard'
import SalonAvailabilityModal from '../ui/SalonAvailabilityModal'
import debug from '../../utils/debug'
import { useUser } from '../../context/UserContext'

const StockOverview = () => {
  const { getAccessibleOperators } = useUser()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Operator configurations - filter based on user access
  const allOperators = [
    { code: 'PLUS', name: 'Plus', color: '#00a651' },
    { code: 'ORANGE', name: 'Orange', color: '#ff6600' },
    { code: 'TMOBILE', name: 'T-Mobile', color: '#e20074' },
    { code: 'P4', name: 'Play', color: '#5f2c82' }
  ]
  
  const operators = allOperators.filter(op => 
    getAccessibleOperators(allOperators.map(o => o.code)).includes(op.code)
  )
  
  const [selectedOperator, setSelectedOperator] = useState(operators[0]?.code || 'PLUS')
  const [specialOnly, setSpecialOnly] = useState(false) // For operators with special attention filter - default OFF
  const [stockData, setStockData] = useState([])
  const [specialReportData, setSpecialReportData] = useState([])
  const [selectedPlant, setSelectedPlant] = useState(null)
  const [plantProducts, setPlantProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [operatorProducts, setOperatorProducts] = useState([]) // Dynamic products list
  
  // Availability modal state
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false)
  const [selectedAvailabilityPlant, setSelectedAvailabilityPlant] = useState(null)
  const [selectedAvailabilitySalon, setSelectedAvailabilitySalon] = useState(null)

  // Export state
  const [exporting, setExporting] = useState(false)

  const currentOperator = operators.find(op => op.code === selectedOperator) || operators[0]

  // Fetch stock data
  const fetchStockData = async (operatorCode, specialOnly = true) => {
    try {
      setLoading(true)
      setError(null)
      
      debug.log('StockOverview', `Fetching stock data for ${operatorCode}, specialOnly: ${specialOnly}`)
      
      const response = await apiService.getStockOverview(operatorCode, specialOnly)
      const data = response.data || []
      
      debug.log('StockOverview', `Stock data received:`, data)
      setStockData(data)
      
    } catch (err) {
      debug.error('StockOverview', 'Error fetching stock data:', err)
      setError(`Błąd pobierania danych zatowarowania: ${err.message}`)
      setStockData([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch special report data (additional details)
  const fetchSpecialReportData = async (operatorCode) => {
    try {
      debug.log('StockOverview', `Fetching special report for ${operatorCode}`)
      
      const response = await apiService.getStockSpecialReport(operatorCode)
      const data = response.data || []
      
      debug.log('StockOverview', `Special report data received:`, data)
      setSpecialReportData(data)
      
    } catch (err) {
      debug.warn('StockOverview', 'Error fetching special report (may not be available for this operator):', err)
      setSpecialReportData([])
    }
  }

  // Fetch plant products (lazy loaded)
  const fetchPlantProducts = async (operatorCode, plantCode) => {
    try {
      setLoadingProducts(true)
      debug.log('StockOverview', `Fetching products for plant ${plantCode}`)
      
      const response = await apiService.getPlantStockProducts(operatorCode, plantCode)
      const data = response.data || []
      
      debug.log('StockOverview', `Plant products received:`, data)
      setPlantProducts(data)
      
    } catch (err) {
      debug.error('StockOverview', 'Error fetching plant products:', err)
      setPlantProducts([])
    } finally {
      setLoadingProducts(false)
    }
  }

  // Fetch operator products when operator changes
  useEffect(() => {
    const fetchOperatorProducts = async () => {
      try {
        debug.log('StockOverview', `Fetching products for ${selectedOperator}`)
        const response = await apiService.getOperatorProducts(selectedOperator)
        const products = response.data || []
        debug.log('StockOverview', `Products for ${selectedOperator}:`, products)
        setOperatorProducts(products)
      } catch (err) {
        debug.warn('StockOverview', `Error fetching products for ${selectedOperator}:`, err)
        setOperatorProducts([])
      }
    }
    
    fetchOperatorProducts()
  }, [selectedOperator])

  // Load data when operator or filters change
  useEffect(() => {
    fetchStockData(selectedOperator, specialOnly)
    
    // Only fetch special report for PLUS (for now)
    if (selectedOperator === 'PLUS') {
      fetchSpecialReportData(selectedOperator)
    } else {
      setSpecialReportData([])
    }
    
    // Clear selected plant when operator changes
    setSelectedPlant(null)
    setPlantProducts([])
  }, [selectedOperator, specialOnly])

  // Handle plant selection
  const handlePlantSelect = (plantCode) => {
    setSelectedPlant(plantCode)
    if (plantCode) {
      fetchPlantProducts(selectedOperator, plantCode)
    } else {
      setPlantProducts([])
    }
  }

  // Handle availability details click
  const handleAvailabilityClick = (plantCode, salonName) => {
    setSelectedAvailabilityPlant(plantCode)
    setSelectedAvailabilitySalon(salonName)
    setAvailabilityModalOpen(true)
  }

  // Handle export to XLSX
  const handleExport = async () => {
    if (exporting) return
    
    setExporting(true)
    try {
      const { blob, filename } = await apiService.exportStockOverview(selectedOperator, specialOnly)
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
    } catch (error) {
      console.error('Export error:', error)
      setError(`Błąd eksportu: ${error.message}`)
    } finally {
      setExporting(false)
    }
  }

  // Calculate metrics
  const calculateMetrics = () => {
    if (!stockData.length) return {}

    const totalSalons = stockData.length
    const needsAttention = stockData.filter(item => item.requires_special_attention).length
    const hasVisualization = stockData.filter(item => item.stock_summary_any_visualization).length
    const avgStock = stockData.reduce((sum, item) => sum + (item.estimated_stock_today || 0), 0) / totalSalons
    const lowStockCount = stockData.filter(item => (item.estimated_stock_today || 0) < 5).length
    
    // Calculate average availability across all salons
    // Treat null as 0% (salons without stock data have 0% availability)
    const avgAvailability = stockData.reduce((sum, item) => {
      const availability = item.availability_pct_30d !== null && item.availability_pct_30d !== undefined 
        ? item.availability_pct_30d 
        : 0
      return sum + availability
    }, 0) / totalSalons

    return {
      totalSalons,
      needsAttention,
      hasVisualization,
      avgStock: avgStock.toFixed(1),
      lowStockCount,
      attentionPct: ((needsAttention / totalSalons) * 100).toFixed(1),
      visualizationPct: ((hasVisualization / totalSalons) * 100).toFixed(1),
      avgAvailability: (avgAvailability * 100).toFixed(1)
    }
  }

  const metrics = calculateMetrics()

  // Stock table columns
  const getStockColumns = () => {
    const baseColumns = [
    {
      field: 'salon_name',
      headerName: 'Salon',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'city',
      headerName: 'Miasto',
      flex: 0.8,
      minWidth: 120
    },
    {
      field: 'plant_code',
      headerName: 'Plant Code',
      flex: 0.8,
      minWidth: 100
    },
    {
      field: 'requires_special_attention',
      headerName: 'Wymaga uwagi',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'TAK' : 'NIE'}
          color={params.value ? 'error' : 'success'}
          size="small"
        />
      )
    },
    {
      field: 'estimated_stock_today',
      headerName: 'Szac. stan dziś',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography 
          color={params.value < 5 ? 'error' : 'inherit'}
          fontWeight={params.value < 5 ? 'bold' : 'normal'}
        >
          {params.value?.toLocaleString() || 0}
        </Typography>
      )
    },
    {
      field: 'availability_pct_30d',
      headerName: '% dostępności (30d)',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => {
        const pct = params.value ? (params.value * 100) : 0
        return (
          <Button
            variant="text"
            size="small"
            startIcon={<Info />}
            onClick={(e) => {
              e.stopPropagation() // Prevent row click
              handleAvailabilityClick(params.row.plant_code, params.row.salon_name)
            }}
            sx={{
              color: pct < 50 ? 'error.main' : pct < 80 ? 'warning.main' : 'success.main',
              fontWeight: 'medium',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            {pct.toFixed(0)}%
          </Button>
        )
      }
    },
    {
      field: 'stock_summary_any_visualization',
      headerName: 'Wizualizacja (KAM)',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'TAK' : 'NIE'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'operator_visualization',
      headerName: 'Wizualizacja (operator)',
      flex: 0.6,
      minWidth: 130,
      renderCell: (params) => (
        <Chip
          label={params.value === true ? 'TAK' : params.value === false ? 'NIE' : '-'}
          color={params.value === true ? 'primary' : params.value === false ? 'default' : 'default'}
          size="small"
          variant={params.value === null || params.value === undefined ? 'outlined' : 'filled'}
        />
      )
    },
    {
      field: 'last_visit_date',
      headerName: 'Ostatnia wizyta',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        if (!params.value) return '-'
        const date = new Date(params.value)
        return date.toLocaleDateString('pl-PL')
      }
    },
    {
      field: 'last_visit_kam_name',
      headerName: 'KAM',
      flex: 1,
      minWidth: 120
    }
  ]

  // Add per-product columns dynamically for any operator with products
  if (operatorProducts.length > 0) {
    const productColumns = []
    
    operatorProducts.forEach(product => {
      // Stock column for this product
      productColumns.push({
        field: `${product.product_code}_stock`,
        headerName: `${product.product_name} - Stock`,
        flex: 0.7,
        minWidth: 120,
        renderCell: (params) => {
          const productData = params.row.stock_products?.find(p => p.product_name === product.product_name)
          const quantity = productData?.quantity || 0
          return (
            <Typography 
              color={quantity === 0 ? 'error' : 'inherit'}
              fontWeight={quantity === 0 ? 'bold' : 'normal'}
            >
              {quantity}
            </Typography>
          )
        }
      })
      
      // Availability column for this product (only if operator has availability data)
      if (selectedOperator === 'PLUS' || selectedOperator === 'P4' || selectedOperator === 'ORANGE') {
        productColumns.push({
          field: `${product.product_code}_availability`,
          headerName: `${product.product_name} - Dostępność`,
        flex: 0.8,
        minWidth: 140,
        renderCell: (params) => {
            const productData = params.row.stock_products?.find(p => p.product_name === product.product_name)
            const pct = productData?.availability_pct_30d ? (productData.availability_pct_30d * 100) : 0
          return (
            <Button
              variant="text"
              size="small"
              startIcon={<Info />}
              onClick={(e) => {
                e.stopPropagation()
                handleAvailabilityClick(params.row.plant_code, params.row.salon_name)
              }}
              sx={{
                color: pct < 50 ? 'error.main' : pct < 80 ? 'warning.main' : 'success.main',
                fontWeight: 'medium',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {pct.toFixed(0)}%
            </Button>
          )
        }
        })
      }
    })
    
    // Insert product columns before last_visit_date and KAM columns
    baseColumns.splice(-2, 0, ...productColumns)
  }

  return baseColumns
}

  // Special report columns (only for PLUS)
  const specialReportColumns = [
    {
      field: 'salon_name',
      headerName: 'Salon',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'city',
      headerName: 'Miasto',
      flex: 0.8,
      minWidth: 120
    },
    {
      field: 'plant_code',
      headerName: 'Plant Code',
      flex: 0.8,
      minWidth: 100
    },
    {
      field: 'sales',
      headerName: 'Sprzedaż (7 dni rob.)',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value?.toLocaleString() || 0
    },
    {
      field: 'has_visualization',
      headerName: 'Wizualizacja',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'TAK' : 'NIE'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'is_trained',
      headerName: 'Przeszkolony',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'TAK' : 'NIE'}
          color={params.value ? 'success' : 'warning'}
          size="small"
        />
      )
    },
    {
      field: 'estimated_stock_today',
      headerName: 'Ilość towaru',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => params.value?.toLocaleString() || 0
    },
    {
      field: 'availability_pct_30d',
      headerName: '% dostępności (30d)',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => {
        const pct = params.value ? (params.value * 100) : 0
        return `${pct.toFixed(0)}%`
      }
    }
  ]

  // Plant products columns
  const productsColumns = [
    {
      field: 'product_name',
      headerName: 'Produkt',
      flex: 2,
      minWidth: 200
    },
    {
      field: 'quantity',
      headerName: 'Ilość',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => params.value?.toLocaleString() || 0
    },
    {
      field: 'has_visualization',
      headerName: 'Wizualizacja',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'TAK' : 'NIE'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'is_top_product',
      headerName: 'TOP10',
      flex: 0.6,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'TAK' : 'NIE'}
          color={params.value ? 'primary' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'updated_at',
      headerName: 'Aktualizacja',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        if (!params.value) return '-'
        const date = new Date(params.value)
        return date.toLocaleDateString('pl-PL')
      }
    }
  ]

  if (loading && !stockData.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error && !stockData.length) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        📦 Zatowarowanie salonów
      </Typography>

      {/* Operator Selection and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={selectedOperator}
                  onChange={(e) => setSelectedOperator(e.target.value)}
                  label="Operator"
                >
                  {operators.map((op) => (
                    <MenuItem key={op.code} value={op.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: op.color,
                            mr: 1
                          }}
                        />
                        {op.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Special attention filter - available for all operators */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                    checked={specialOnly}
                    onChange={(e) => setSpecialOnly(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Tylko salony wymagające specjalnej uwagi"
                />
              </Grid>

            {/* Export button */}
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExport}
                disabled={exporting || loading || stockData.length === 0}
                fullWidth
                sx={{ height: '56px' }}
              >
                {exporting ? 'Eksportowanie...' : 'Eksport XLSX'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Metrics Overview */}
      {stockData.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Łączna liczba salonów"
              value={metrics.totalSalons}
              variant="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Wymaga uwagi"
              value={`${metrics.needsAttention} (${metrics.attentionPct}%)`}
              variant="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Ma wizualizację"
              value={`${metrics.hasVisualization} (${metrics.visualizationPct}%)`}
              variant="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Śr. stan towaru"
              value={metrics.avgStock}
              variant="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Śr. dostępność (30d)"
              value={`${metrics.avgAvailability}%`}
              variant={parseFloat(metrics.avgAvailability) >= 80 ? 'success' : 
                       parseFloat(metrics.avgAvailability) >= 50 ? 'warning' : 'error'}
            />
          </Grid>
        </Grid>
      )}

      {/* Stock Overview Table */}
      <Box sx={{ mb: 3 }}>
        <Section title={`Przegląd zatowarowania — ${currentOperator.name}`}>
          {stockData.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Brak danych o zatowarowaniu dla wybranego operatora i filtrów.
            </Alert>
          ) : (
            <ReportTable
              rows={stockData.map((row, index) => ({ id: index, ...row }))}
              columns={getStockColumns()}
              pageSize={25}
              pageSizeOptions={[10, 25, 50]}
              onRowClick={(row) => handlePlantSelect(row.plant_code)}
              initialSort={{ field: 'estimated_stock_today', dir: 'desc' }}
              density="compact"
            />
          )}
        </Section>
      </Box>

      {/* Special Report for PLUS */}
      {selectedOperator === 'PLUS' && specialReportData.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Section title={`Szczegółowy raport — ${currentOperator.name}`}>
            <ReportTable
              rows={specialReportData.map((row, index) => ({ id: index, ...row }))}
              columns={specialReportColumns}
              pageSize={25}
              pageSizeOptions={[10, 25, 50]}
              density="compact"
            />
          </Section>
        </Box>
      )}

      {/* Plant Product Details */}
      {selectedPlant && (
        <Section title={`Szczegóły per produkt — Plant ${selectedPlant}`}>
          {loadingProducts ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : plantProducts.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Brak szczegółowych danych o produktach dla tego salonu.
            </Alert>
          ) : (
            <ReportTable
              rows={plantProducts.map((row, index) => ({ id: index, ...row }))}
              columns={productsColumns}
              pageSize={10}
              pageSizeOptions={[5, 10, 20]}
              density="compact"
            />
          )}
        </Section>
      )}

      {/* Availability Details Modal */}
      <SalonAvailabilityModal
        open={availabilityModalOpen}
        onClose={() => setAvailabilityModalOpen(false)}
        operatorCode={selectedOperator}
        plantCode={selectedAvailabilityPlant}
        salonName={selectedAvailabilitySalon}
      />
    </Box>
  )
}

export default StockOverview